import { type LexicalEmailTemplate } from '../../../../../models/TemplateLibrary'
import { compileEmail } from '../compile/compile'
import { frameHasContentSlot, frameMarkup, inertFragment, wrapperHasContentSlot, wrapperMarkup } from '../compile/exportMap'
import { CONTENT_MARKER } from '../nodes/wrapperStyle'
import { LEXICAL_EMAIL_FORMAT } from '../emailTemplateFormat'
import { createEmailEditor } from '../nodes'
import { checkJinja, validateCompiled } from './jinjaAllowlist'
import { descendants, documentError, emailOf, type LexNode, rootOf } from './validateState'

export type ParsedTemplate = { kind: 'email'; record: LexicalEmailTemplate } | { kind: 'invalid'; reason: string }

const SCRIPT_CAPABLE = ['script', 'iframe', 'object', 'embed', 'base', 'form', 'meta', 'noscript', 'template', 'svg', 'math']
const URL_ATTRIBUTES = ['href', 'src', 'xlink:href', 'srcset', 'background', 'poster', 'action', 'formaction']
const SAFE_ATTRIBUTE_URL = /^\s*(https?:|mailto:|\{\{)/i

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)
const asString = (value: unknown): string => (typeof value === 'string' ? value : '')
const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : [])

export const parseLexicalEmailTemplate = (template: unknown): ParsedTemplate => {
  if (!isObject(template)) {
    return { kind: 'invalid', reason: 'template is not an object' }
  }
  const hasStrings = typeof template.subject === 'string' && typeof template.body === 'string'
  if (template.format !== LEXICAL_EMAIL_FORMAT) {
    return { kind: 'invalid', reason: 'format is not lexical-email' }
  }
  if (template.schemaVersion !== 1) {
    return { kind: 'invalid', reason: 'schemaVersion is not 1' }
  }
  if (!hasStrings) {
    return { kind: 'invalid', reason: 'subject or body is missing' }
  }
  if (!isObject(template.editor) || !isObject(template.editor.state) || !isObject(template.editor.state.root)) {
    return { kind: 'invalid', reason: 'editor.state is missing' }
  }
  return { kind: 'email', record: template as unknown as LexicalEmailTemplate }
}

const elementsOf = (html: string, where: string): Element[] =>
  Array.from((where === 'frame' ? new DOMParser().parseFromString(html, 'text/html') : inertFragment(html)).querySelectorAll('*'))

const htmlProblems = (html: string, where: string): string[] =>
  elementsOf(html, where).flatMap((element) => {
    const tag = element.tagName.toLowerCase()
    const attributeProblems = Array.from(element.attributes).flatMap((attribute) => {
      const name = attribute.name.toLowerCase()
      return [
        ...(name.startsWith('on') ? [`${where}: ${attribute.name} is not allowed`] : []),
        ...(URL_ATTRIBUTES.includes(name) && !SAFE_ATTRIBUTE_URL.test(attribute.value) ? [`${where}: ${attribute.name} must be http, https or mailto`] : []),
      ]
    })
    return [...(SCRIPT_CAPABLE.includes(tag) ? [`${where}: <${tag}> is not allowed`] : []), ...attributeProblems]
  })

const nodesOf = (record: LexicalEmailTemplate): LexNode[] => {
  const root = rootOf(record.editor.state)
  return root ? descendants(root) : []
}

const htmlFields = (record: LexicalEmailTemplate): Array<[string, string]> =>
  nodesOf(record).flatMap((node): Array<[string, string]> => {
    switch (node.type) {
      case 'email':
        return [
          ['frame', frameMarkup(asString(node.frame))],
          ...Object.entries(isObject(node.textStyles) ? node.textStyles : {}).map(([tag, css]): [string, string] => [
            `textStyles.${tag}`,
            `<p style="${asString(css).replace(/"/g, '&quot;')}"></p>`,
          ]),
        ]
      case 'email-slot':
        return [[asString(node.slotId), wrapperMarkup(asString(node.wrapper))]]
      case 'email-group':
        return [[asString(node.groupId), wrapperMarkup(asString(node.wrapper))]]
      case 'email-locked':
        return [[asString(node.blockId), asString(node.html)]]
      default:
        return []
    }
  })

export const checkRecordHtml = (record: LexicalEmailTemplate): string[] => htmlFields(record).flatMap(([where, html]) => htmlProblems(html, where))

const variableNames = (record: LexicalEmailTemplate): string[] => (emailOf(record.editor.state)?.variables ?? []).map((variable) => variable.name)

const nodeName = (node: LexNode): string => asString(node.slotId) || asString(node.groupId) || asString(node.blockId) || node.type

const badgeProblems = (record: LexicalEmailTemplate, names: string[]): string[] =>
  nodesOf(record).flatMap((node) =>
    asArray<unknown>(node.badges)
      .flatMap((badge) => asArray<{ type?: unknown; variable?: unknown }>(isObject(badge) ? badge.children : []))
      .filter((child) => child.type === 'variable' && !names.includes(asString(child.variable)))
      .map((child) => `${nodeName(node)}: the badge uses ${asString(child.variable)}, which isn't a variable of this email`),
  )

const markerCount = (html: string): number => html.split(CONTENT_MARKER).length - 1

const markerProblems = (record: LexicalEmailTemplate): string[] =>
  nodesOf(record).flatMap((node): string[] => {
    const isFrame = node.type === 'email'
    const wrapper = asString(isFrame ? node.frame : node.wrapper)
    const checked = isFrame || node.type === 'email-group' || node.type === 'email-slot'
    if (!checked || (node.type === 'email-slot' && wrapper === '')) {
      return []
    }
    const slotted = isFrame ? frameHasContentSlot(wrapper) : wrapperHasContentSlot(wrapper)
    return markerCount(wrapper) === 1 && slotted ? [] : [`${nodeName(node)}: needs exactly one ${CONTENT_MARKER} in its markup`]
  })

const parseError = (record: LexicalEmailTemplate): string | null => {
  try {
    createEmailEditor().parseEditorState(JSON.stringify(record.editor.state))
    return null
  } catch (error) {
    return `editor.state: ${error instanceof Error ? error.message : String(error)}`
  }
}

const compiledProblem = (record: LexicalEmailTemplate, names: string[]): string | null => {
  try {
    const compiled = compileEmail(record.editor.state)
    const problems = validateCompiled(compiled.subject, compiled.body, names)
    return problems.length ? `compiled: ${problems[0]}` : null
  } catch (error) {
    return `compiled: ${error instanceof Error ? error.message : String(error)}`
  }
}

const checkParsedRecord = (template: unknown): ParsedTemplate => {
  const parsed = parseLexicalEmailTemplate(template)
  if (parsed.kind !== 'email') {
    return parsed
  }
  const document = documentError(parsed.record.editor.state)
  if (document) {
    return { kind: 'invalid', reason: document.message }
  }
  const unparsable = parseError(parsed.record)
  if (unparsable) {
    return { kind: 'invalid', reason: unparsable }
  }
  const names = variableNames(parsed.record)
  const problems = [
    ...checkRecordHtml(parsed.record),
    ...badgeProblems(parsed.record, names),
    ...markerProblems(parsed.record),
    ...htmlFields(parsed.record).flatMap(([where, html]) => checkJinja(html, names).map((problem) => `${where}: ${problem}`)),
    ...checkJinja(parsed.record.subject, names).map((problem) => `subject: ${problem}`),
    ...checkJinja(parsed.record.body, names).map((problem) => `body: ${problem}`),
  ]
  if (problems.length) {
    return { kind: 'invalid', reason: problems[0] }
  }
  const compiled = compiledProblem(parsed.record, names)
  return compiled ? { kind: 'invalid', reason: compiled } : parsed
}

export const checkRecord = (template: unknown): ParsedTemplate => {
  try {
    return checkParsedRecord(template)
  } catch (error) {
    return { kind: 'invalid', reason: `malformed record: ${error instanceof Error ? error.message : String(error)}` }
  }
}
